/*** IMPORTS FROM imports-loader ***/
var define = false;

"use strict";

var ko = require("knockout");
var R = require("ramda");
var Rx = require("rxjs");

var DataState = require("../data-provider/dataProvider").DataState;
var disposeObject = require("../util/disposable").disposeObject;
var fromKnockoutObservable = require("../util/rx").fromKnockoutObservable;
var GridViewModel = require("../ko-grid/knockoutGridViewModel");
var QueryOrigin = require("../data-provider/dataProvider").QueryOrigin;
var segmentToClasses = require("../util/segmentToClasses").default;
var stateOfResponse = require("../data-provider/dataProvider").stateOfResponse;


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Private
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

/**
 * Default options to be used by the ko-grid.
 * @type {Object}
 */
var defaultGridOptions = {
    autoHeight: false,
    disabled: false,
    forceFitColumns: true,
    multiSelect: false,
    sortedColumns: []
};


/**
 * Default field name for identifier columns.
 * @type {String}
 */
var defaultIdField = "id";


/**
 * Full list of supported ko-grid properties.
 * @type {Array}
 */
var koGridProperties = [
    "autoHeight",
    "columns",
    "disabled",
    "factories",
    "focusedItem",
    "forceFitColumns",
    "idField",
    "items",
    "multiSelect",
    "scrollTop",
    "selectedItems",
    "shortcutActions",
    "sortedColumns"
];


/**
 * Translates a 'groups' object from the view model into a
 * grid-compatible group factory, or null if no 'groups' entry
 * exists for the given column ID.
 * @param  {Object} col      The column we are determining grouping for
 * @param  {Object} gridData The grid data passed by the view model
 * @return {Object}          A ko-grid group object or null
 */
function getGrouping(col, gridData) {
    var canBeGrouped = gridData.groups ? gridData.groups[col.id] !== undefined : false;
    return canBeGrouped ? { formatter: "value:group", getter: col.field.toString() + ":default" } : null;
}


/**
 * Populates the property specified by 'idField' for a list of items.
 * If missing, uses the 0-based index as an identifier.
 */
function convertGridItems(legacyItems, idField) {
    if (idField === null || idField === undefined) { idField = defaultIdField; }

    return legacyItems.map(function (legacyItem, index) {
        var item = R.clone(legacyItem);
        if (!item[idField]) {
            item[idField] = index.toString();
        }
        return item;
    });
}


/**
 * Converts picklist grid data into the format expected by ko-grid.
 * @param  {Object} gridData
 *      Data from the view model
 * @param  {Object} options
 *      Current options from the ko-grid
 * @return {Object}
 *      Options to be passed to the ko-grid
 */
function convertGridData(gridData, options) {
    if (!gridData.columns && (!gridData.views || !gridData.views.standard)) {
        throw new Error("No picklist grid column specification was provided (required).");
    }

    var gridOptions = R.clone(options);

    // if gridData does not contain a columns attribute
    // i.e. is not providing ko-grid styled data, then
    // convert views to usable columns
    gridOptions.columns = gridData.columns || gridData.views.standard.map(function (legacyColumn) {
        return {
            id: legacyColumn.id.toString(),
            title: legacyColumn.name,
            field: legacyColumn.field.toString(),
            group: getGrouping(legacyColumn, gridData) || undefined,
            sortable: legacyColumn.sortable,
            width: legacyColumn.width
        };
    });

    gridOptions.items = convertGridItems(gridData.items, gridOptions.idField);

    return gridOptions;
}


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// "PicklistFormViewModel" class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

/**
 * Create a new instance of picklist form viewmodel.
 *
 * @param {Function} closeForm
 *      An action that will hide or close the picklist form on screen.
 */
function PicklistFormViewModel(closeForm) {
    if (!(this instanceof PicklistFormViewModel)) { return new PicklistFormViewModel(closeForm); } // if new was not used, force it now
    if (typeof closeForm !== "function") { throw new Error("No \"closeForm\" (Forms.js close function) was specified or is not referencing a function (required)"); }

    // private
    this._closeForm = closeForm;
    this._containerCss = ko.observable(null);
    this._error = ko.observable(null);
    this._gridInitialSelection = null;
    this._gridView = null;
    this._hiddenSelectedItems = []; // preserves selected items hidden by a filter
    this._initialDataSubscription = null;
    this._itemFilter = null;
    this._name = ko.observable(null);
    this._options = ko.observable(null);
    this._searchStreamSubscription = null;
    this._selectionSubscription = null;
    this._text = ko.observable(null);
    this._totalGridItemsCount = ko.pureComputed(this._getTotalGridItemsCount, this);

    // view properties
    this.canAccept = ko.pureComputed(this._getCanAccept, this);
    this.createItemVisible = ko.observable(false);
    this.filter = ko.observable("");
    this.filterFocused = ko.observable(true);
    this.grid = new GridViewModel(defaultGridOptions, { defaultAction: R.bind(this._onDoubleClickOrEnter, this) });
    this.openSelectedItemVisible = ko.observable(false);
    this.previewViewModel = ko.observable(null);
    this.previewTemplateId = ko.observable(this._emptyTemplateId);
    this.previewTemplateId.subscribe(this._previewCleanupGuard, this);
    this.searchStream = null;
    this.selectionInfo = ko.pureComputed(this._getSelectionInfo, this);
    this.state = ko.observable(null);

    // public functions
    this.resolveContainerCssWithState = segmentToClasses.bind(null, this.state, this._containerCss, "withState");
}


/**
 * Accept currently selected value and notify result receiver with this selected value.
 * Then unsubscribe from data provider and close/hide the form on screen (using action specified in ctor).
 */
PicklistFormViewModel.prototype.accept = function acceptPicklist() {
    if (this.canAccept.peek()) {
        var selectedItems = this.getSelectedRowsAsItems();
        if (selectedItems && (this.grid.options.peek().multiSelect || selectedItems.length)) {
            this._closeAndNotifyResultReceiver(selectedItems);
        }
    }

    return false;
};


/**
 * Cancel this picklist by unsubscribing from data provider and closing/hiding the form on screen.
 * Uses action specified in ctor.
 */
PicklistFormViewModel.prototype.cancel = function cancelPicklist() {
    // careful; we need to notify result receiver
    // even though we have no result for it
    // (using special value, "undefined");
    // this will unblock editing of the underlying
    // field/observable
    this._closeAndNotifyResultReceiver();
    return false;
};


/**
 * If options.createItem was specified, call it now using the view model instance as "this".
 */
PicklistFormViewModel.prototype.createItem = function createItemForPicklist() {
    var createItem = this._options().createItem;
    if (typeof createItem === "function" && this.state().indexOf("data.") === 0) {
        createItem.call(this);
    }
    return false;
};


/**
 * Get all selected grid items.
 *
 * @return {Object[]}
 *      The items currently selected in the grid, including those hidden by a filter.
 */
PicklistFormViewModel.prototype.getSelectedRowsAsItems = function getSelectedPicklistRowsAsItems() {
    if (this.grid.options.peek().multiSelect) {
        var items = this.grid.selectedItems() || [];
        return items.concat(this._hiddenSelectedItems);
    }

    var focusedItem = this.grid.focusedItem();
    return focusedItem ? [focusedItem] : [];
};


/**
 * If options.openSelectedItem was specified call it now.
 * @this {PicklistFormViewModel} Viewmodel instance.
 */
PicklistFormViewModel.prototype.openSelectedItem = function openSelectedItemForPicklist() {
    var openSelectedItem = this._options().openSelectedItem;
    if (typeof openSelectedItem === "function" && this.state().indexOf("data.selected.") === 0) {
        var selectedItems = this.getSelectedRowsAsItems();
        openSelectedItem.call(this, selectedItems);
    }
    return false;
};


/**
 * Execute progress action (button that is shown when progress/spinner frame shows an error).
 * (E.g. when data pull failed or returned data set was empty).
 * Actual action is dependent on the current state.
 */
PicklistFormViewModel.prototype.progressAction = function progressActionForPicklist() {
    switch (this.state()) {
    case "load.done.error":
        if (typeof this._options().data.reload === "function") {
            this._options().data.reload.apply(this);
        }
        break;
    }

    return false;
};


/**
 * Reset this viewmodel to a "load.active" state, updating texts as specified by options and unsubscribing from data provider.
 * Will be called before picklist form is shown on screen.
 * @param {Object} options
 * @param {Object} options.dataProvider
 *      Data provider.
 * @param {Function} options.value
 *      Result receiver.
 */
PicklistFormViewModel.prototype.reset = function resetPicklist(options) {
    if (!options) { throw new Error("No \"options\" were specified (required)"); }
    if (!options.dataProvider || !options.dataProvider.search) { throw new Error("No \"options.dataProvider\" (data provider) was specified or it does not conform to the DataProvider interface (required)"); }
    if (!options.value || typeof options.value !== "function") { throw new Error("No options.value (result receiver) was specified or it is not an observable/function (required)"); }
    if (typeof options.itemFilterBuilder === "function") { throw new Error("options.itemFilterBuilder is no longer supported. Use a custom DataProvider implementation to customize filtering."); }
    if (typeof options.filterBuilder === "function") { throw new Error("options.filterBuilder is no longer supported. Use a custom DataProvider implementation to customize filtering."); }
    if (options.idField === null || options.idField === undefined) {
        // will default to 'id' if unspecified. If 'id' does not exist, it will
        // create a row index-based key since ko-grid requires a unique id column
        options.idField = defaultIdField;
    }

    this.previewTemplateId(this._emptyTemplateId);
    this.openSelectedItemVisible(typeof options.openSelectedItem === "function");
    this.createItemVisible(typeof options.createItem === "function");

    // initialize preview (if specified in options)
    if (typeof options.previewInit === "function") {
        if (this.previewViewModel.peek() !== null) { throw new Error("Preview viewmodel has not been cleaned (by last preview)"); }
        var previewViewModel = options.previewInit(this);
        if (!previewViewModel) { throw new Error("ViewModel returned by \"options.previewInit\" must be truthy"); }
        if (typeof previewViewModel.templateId !== "string") { throw new Error("ViewModel returned by \"options.previewInit\" must contain a string property \"templateId\""); }
        this.previewViewModel(previewViewModel);
        this.previewTemplateId(previewViewModel.templateId);
    }

    var css = (this.previewTemplateId.peek() === this._emptyTemplateId ? null : "picklistPreview");
    css = (options.hideFilter ? (css ? css.concat(" hideFilter") : "hideFilter") : css);
    css = (options.containerCss ? (css ? css.concat(" ", options.containerCss) : options.containerCss) : css);
    this._containerCss(css);

    var text = { };
    ko.utils.extend(text, this._textDefaults);
    ko.utils.extend(text, options.text);
    this._text(text);

    this._hiddenSelectedItems = [];
    this._gridInitialSelection = options.selectedItems && options.selectedItems.length ? options.selectedItems : [0];
    this._gridView = options.view;

    this._options(options);

    // initialize grid
    var gridOptions = R.merge(defaultGridOptions, options);

    // selected items will be updated in _onInitialResponse
    gridOptions.selectedItems = undefined;
    gridOptions.focusedItem = null;

    this.grid.apply(R.pick(koGridProperties, gridOptions));

    this.state("load.active");

    this.filter(options.filterValue || "");
    this.searchStream = this
        ._createSearchStream(fromKnockoutObservable(this.filter, /* omitInitialValue */ true)) // initial value is already handled by "dataProvider.load"
        .publish()
        .refCount(); // reference-counted hot observable (created on 1st subscription, disposed on last unsubscription)

    // subscribe to initial data load
    var initialRequest = Rx.Observable
        .of({ filter: options.filterValue })
        .delay(0, options.dataProvider.scheduler); // defer until the next JavaScript engine tick to ensure ko-grid is rendered when visible

    if (this._initialDataSubscription) { this._initialDataSubscription.unsubscribe(); }
    this._initialDataSubscription = options.dataProvider
        .search(initialRequest, QueryOrigin.Initial)
        .subscribe(this._onInitialResponse.bind(this));

    // subscribe to filter changes
    if (this._searchStreamSubscription) { this._searchStreamSubscription.unsubscribe(); }
    this._searchStreamSubscription = this.searchStream
        .subscribe(this._onFilterResponse.bind(this));

    // subscribe to selection changes
    if (this._selectionSubscription) { this._selectionSubscription.dispose(); }
    this._selectionSubscription = this.selectionInfo.subscribe(this._onSelectionInfoChanged, this);
};


/**
 * Compute visibility flag for progress action (button that is shown when progress/spinner frame shows an error).
 */
PicklistFormViewModel.prototype.resolveProgressActionVisible = function resolveProgressActionVisibleForPicklist() {
    switch (this.state()) {
    case "load.done.error":
        return typeof this._options().data.reload === "function";
    }

    return false;
};


/**
 * Find text for the specified key in "text()" observable.
 * If no mapping for key can be found, returns an empty string.
 *
 * "text()" observable is expected to be an object containing keys and values as either
 * strings or functions. To resolve keys mapped to functions, "resolveText" will return value
 * the mapped function returns. The functions will be called with "this" pointing to
 * this viewmodel instance.
 *
 * Any observables read by either "resolveText" or mapped resolver function will be added
 * to computed observable dependencies that view binding creates, i.e. if any of those
 * values changes, resolveText for that key will be called again. If this behaviour is
 * undesirable, use observable's "peek" method.
 */
PicklistFormViewModel.prototype.resolveText = function resolveTextForPicklist(key) {
    if (key) {
        var text = this._text();
        if (text) {
            var item = text[key];
            if (item) {
                if (typeof item === "function") { return item.apply(this); }
                return item.toString();
            }
        }
    }
    return "";
};


/**
 * Close/hide the form on screen, then notify result receiver that picklist form has been
 * dismissed (either accepting selected value or cancelling).
 */
PicklistFormViewModel.prototype._closeAndNotifyResultReceiver = function closePicklistFormAndNotifyResultReceiver(result) {
    if (this._selectionSubscription) {
        this._selectionSubscription.dispose();
        this._selectionSubscription = null;
    }
    if (this._initialDataSubscription) {
        this._initialDataSubscription.unsubscribe();
        this._initialDataSubscription = null;
    }
    if (this._searchStreamSubscription) {
        this._searchStreamSubscription.unsubscribe();
        this._searchStreamSubscription = null;
        this.filter("");
    }
    this.previewTemplateId(this._emptyTemplateId);

    // To resolve an issue with usage of multi depth pick list dialogs
    // Issue arises when setting the value of a picklist of a picklist
    // When "_closeForm()" is executed the previous pick list form state
    // is loaded thus when setting the value afterwards, it sets the
    // value in the wrong field.
    // This solution keeps a reference to the correct field then setting
    // the value after closing the form
    var resultReceiver = this._options.peek().value;

    this._closeForm();

    // careful; result receiver must be notified after
    // the form has closed (on screen, by Forms.js)
    // because the update of the picklist value
    // could trigger hash change, which is
    // not allowed by Forms.js
    resultReceiver(result);
};


/**
 * Creates a search result stream from the filter value stream.
 * @param {Rx.Observable} requestStream
 *      Observable stream containing user filter requests.
 * @return {Rx.Observable}
 *      Search result stream.
 */
PicklistFormViewModel.prototype._createSearchStream = function createSearchStream(requestStream) {
    var viewModel = this;
    var dataProvider = this._options().dataProvider;

    return dataProvider.search(
        requestStream.map(function createRequest(filter) {
            var options = viewModel._options();

            // populate 'currentValue' based on currently focused item
            // or the initial selection; only practical for single-select
            // as this may be used to ensure the results that are returned
            // contain the provided value
            var currentValue = null;
            if (!options.multiSelect) {
                var focusedItem = viewModel.grid.focusedItem();
                if (focusedItem) {
                    currentValue = focusedItem[options.idField];
                } else if (viewModel._gridInitialSelection && typeof viewModel._gridInitialSelection[0] === "object") {
                    currentValue = viewModel._gridInitialSelection[0][options.idField];
                }
            }

            return { filter: filter, currentValue: currentValue };
        }),
        QueryOrigin.Grid,
        {
            debounceTime: this._options().filterDebounceTimeout
        }
    );
};


/**
 * ID of empty template element (doesn't have any content, used when picklist form is opened without preview template).
 * @type {String}
 */
PicklistFormViewModel.prototype._emptyTemplateId = "picklistFormEmptyTemplate";


/**
 * Gets a value indicating whether the current form can be accepted.
 * @return {boolean} True if the form can be accepted, false otherwise.
 */
PicklistFormViewModel.prototype._getCanAccept = function getPicklistCanAccept() {
    return (this.grid.options().multiSelect
        ? this.state().indexOf("data.") === 0
        : this.state().indexOf("data.selected.") === 0);
};


/**
 * Implements the selectionInfo interface by retrieving the values from the underlying grid.
 * @return {Object} SelectionInfo values
 */
PicklistFormViewModel.prototype._getSelectionInfo = function getPicklistSelectionInfo() {
    // capture selected rows so that any
    // dependent observables are tracked
    var selectedRows = this.getSelectedRowsAsItems();
    return {
        get: R.always(selectedRows),
        count: selectedRows.length
    };
};


/**
 * Gets the total number of grid items.
 */
PicklistFormViewModel.prototype._getTotalGridItemsCount = function getTotalGridItemsCount() {
    var items = this.grid ? this.grid.items() : [];
    return items ? items.length : 0;
};


/**
 * On double click or enter accept the current values selected in the grid.
 * Special case for multi select: On double click select the focused item before accepting.
 * @param  {Object} data
 *      Row Selected
 * @param  {Object} mouseEvent
 *      Coordinates where the mouse double clicked
 */
/*jslint unparam: true */ // needed because 'data' is unused below
PicklistFormViewModel.prototype._onDoubleClickOrEnter = function onPicklistGridDoubleClickOrEnter(data, mouseEvent) {
    // double click for multi select has to add the selection to selections before accept
    var multiSelect = this.grid.options.peek().multiSelect;
    if (multiSelect && mouseEvent) {
        var focusedItem = this.grid.focusedItem();
        var selectedItems = this.grid.selectedItems();
        var newSelectedItems = selectedItems.indexOf(focusedItem) > -1 ? selectedItems : selectedItems.concat([focusedItem]);
        this.grid.apply({ selectedItems: newSelectedItems });
    }

    this.accept();
};
/*jslint unparam: false */


/**
 * Filter grid items and preserve selections hidden by new search term.
 * @param dataResult
 *      Grid results matching new search filter.
 */
PicklistFormViewModel.prototype._onFilterResponse = function onPicklistFilterResponse(dataResult) {
    var resultState = stateOfResponse(dataResult);
    if (resultState === DataState.Error) {
        this._error(dataResult.error);
        this.state("load.done.error");
    } else if (resultState === DataState.Ready) {
        // filter items and reapply to grid
        var idField = this._options.peek().idField;
        var filteredItems = convertGridItems(dataResult.data.items, idField);
        var currentFocusedItem = this.grid.focusedItem.peek();
        var gridOptions = {
            items: filteredItems,
            // if no row has focus after filter, focus the first row
            focusedItem: R.contains(currentFocusedItem, filteredItems)
                ? currentFocusedItem
                : filteredItems[0]
        };

        // preserve selected items (by idField) that may be hidden by the current filter
        if (this.grid.options.peek().multiSelect) {
            var selectedItems = this.getSelectedRowsAsItems();
            var hiddenSelectedItems = R.differenceWith(R.eqProps(idField), selectedItems, filteredItems);
            var newSelectedItems = R.intersectionWith(R.eqProps(idField), selectedItems, filteredItems);

            gridOptions.selectedItems = newSelectedItems;
            this._hiddenSelectedItems = hiddenSelectedItems;
        }

        // indicate the load operation completed
        // this must happen *before* applying grid options to avoid slickgrid rendering glitches
        this.state("data.available");

        this.grid.apply(gridOptions);

        // ensure filter input retains focus after the grid refreshes itself
        this.filterFocused(true);
    }
};


/**
 * Handle initial data results by updating "gridData" and state.
 */
PicklistFormViewModel.prototype._onInitialResponse = function onPicklistInitialResponse(dataResult) {
    // Now update actual grid data
    this._name((dataResult && dataResult.name) || "value");

    var error = (dataResult && typeof dataResult === "object"
        ? dataResult.error
        : "Internal error (missing data provider value)");

    var state = "load.active";
    var value = null;
    var resultState = stateOfResponse(dataResult);
    if (resultState === DataState.Ready) {
        value = dataResult.data;
        if (value) {
            if (typeof value !== "object" || !value.items) {
                error = "Internal error (invalid data provider value)";
            }
        }
    }

    if (error) {
        this._error(error);
        state = "load.done.error";
    }

    this.state(state);

    if (value) {
        // apply custom view and convert grid data to a format that the ko-grid can accept
        var options = convertGridData(
            this._gridView ? R.merge(value, { views: { standard: this._gridView } }) : value,
            this.grid.options.peek()
        );

        // set selected item(s) appropriately
        options.selectedItems = [];
        if (this._gridInitialSelection && this._gridInitialSelection.length > 0) {
            if (typeof this._gridInitialSelection[0] === "number") {
                options.focusedItem = options.items[this._gridInitialSelection[0]];
            } else if (!options.multiSelect) {
                options.focusedItem = convertGridItems(this._gridInitialSelection, options.idField)[0];
            } else {
                options.focusedItem = convertGridItems(this._gridInitialSelection, options.idField)[0];
                options.selectedItems = convertGridItems(this._gridInitialSelection, options.idField);
            }
            this._gridInitialSelection = null;
        } else {
            // try to preserve previous selections based on their id
            // because other values may have changed
            var oldSelectedIds = R.pluck(options.idField, this.getSelectedRowsAsItems());
            var newSelectedItems = R.filter(function (item) {
                return R.contains(item[options.idField], oldSelectedIds);
            }, options.items);

            if (options.multiSelect) {
                options.selectedItems = newSelectedItems;
            }
            if (newSelectedItems.length > 0) {
                options.focusedItem = newSelectedItems[0];
            }
        }

        // indicate the load operation completed
        // this must happen *before* applying grid options to avoid slickgrid rendering glitches
        this.state("data.available");

        this.grid.apply(options);

        this.filterFocused(true); // focus must be *after* grid.apply
    }
};


/**
 * React to ko-grid selection changes by updating state if needed ("data.available" vs. "data.selected.one" vs "data.selected.many").
 */
PicklistFormViewModel.prototype._onSelectionInfoChanged = function onPicklistSelectionInfoChanged(selectionInfo) {
    var selectionsCount = selectionInfo.count;
    if (this.state.peek().indexOf("data.") === 0) {
        this.state(selectionsCount > 0
            ? selectionsCount === 1 ? "data.selected.one" : "data.selected.many"
            : "data.available");
    }
};


/**
 * Make sure that every time "previewTemplateId" observable is reset we dispose and reset "previewViewModel" to null (if defined).
 */
PicklistFormViewModel.prototype._previewCleanupGuard = function picklistPreviewCleanupGuard(previewTemplateIdValue) {
    var previewViewModel = this.previewViewModel.peek();
    if (previewViewModel !== null && previewTemplateIdValue === this._emptyTemplateId) {
        disposeObject(previewViewModel, true); // force-dispose (include "function" containers, i.e. "extras")
        this.previewViewModel(null);
    }
};


/**
 * Define default texts and labels (can be overridden in options.text).
 */
PicklistFormViewModel.prototype._textDefaults = {
    icon: "sprite-pickList-16",
    title: function () {
        var title = "Select " + this._name();
        var totalGridItems = this._totalGridItemsCount();
        return totalGridItems !== null
            ? title + " (" + totalGridItems + ")"
            : title;
    },
    subtitle: function () {
        return (this._options().multiSelect
            ? "Select one or more items from the grid. Then click the \"Accept\" button"
            : "Select an item from the grid. Then click the \"Accept\" button");
    },
    filterPlaceholder: "Search...",
    createItemButton: "New",
    openSelectedItemButton: "Open selected",
    acceptButton: function () {
        var selectionsCount = this.selectionInfo().count;
        return (this._options().multiSelect && selectionsCount > 0
            ? "Accept (" + selectionsCount + ")"
            : "Accept");
    },
    cancelButton: "Cancel",
    progressTitle: function () {
        switch (this.state()) {
        case "load.done.error":
            return "Load failed";
        default:
            return "Loading...";
        }
    },
    progressSubtitle: function () {
        switch (this.state()) {
        case "load.done.error":
            return this._error() || "(No details)";
        default:
            return "Please wait";
        }
    },
    progressActionButton: "Try again"
};


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Initialization
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

module.exports = PicklistFormViewModel;




//////////////////
// WEBPACK FOOTER
// ./~/pli/forms-knockoutcontrols-picklist/picklistFormViewModel.js
// module id = 441
// module chunks = 49